import l from '../../../../../lang';
import { usePayouts } from '../../../../../store/payouts';
import { Filters } from '../../../../../store/interfaces';
import MultiSelect from '../../../../common/multi-select';
import SelectDateRange from '../../../../common/select-date-range';
import {
  segmentTransactionsFilterCountry,
  segmentTransactionsFilterDate,
  segmentTransactionsFilterPaymentStatus,
} from '../../../../../utils/analytics';
import { useAvailableCountries } from '../../../../../hooks/use-available-countries';

interface useComponentsFilterProps {
  appliedFilters: Filters;
  changeFilters: (filters: Filters) => void;
}

const useComponentsFilter = ({ appliedFilters, changeFilters }: useComponentsFilterProps) => {
  const { loading } = usePayouts();
  const allCountries = useAvailableCountries();

  const countries = () => {
    const countries: { id: string; name: string }[] = [];
    allCountries.forEach(co => {
      countries.push({
        name: l(`countryCode.${co}`),
        id: co,
      });
    });
    return countries;
  };

  const payoutStatus = () => {
    return {
      PENDING: l('payouts.status.PENDING'),
      COMPLETED: l('payouts.status.COMPLETED'),
      CANCELLED: l('payouts.status.CANCELLED'),
      REJECTED: l('payouts.status.REJECTED'),
      FAILED: l('payouts.status.FAILED'),
      ON_HOLD: l('payouts.status.ON_HOLD'),
      PROCESSING: l('payouts.status.PROCESSING'),
    };
  };

  const status = () => {
    const status: { id: string; name: string }[] = [];
    const ts: { [key: string]: string } = payoutStatus();
    Object.keys(ts).forEach(s => {
      status.push({
        name: ts[s],
        id: s,
      });
    });
    return status;
  };

  return {
    statusFilter: (
      <MultiSelect
        value={appliedFilters.status ? appliedFilters.status.split(',').map(s => status().find(ts => `${ts.id}` === s)) : []}
        name="filter-status"
        size="sm"
        label={l('transactions.filter.status')}
        titleOptions={l('transactions.filter.status.titleOptions')}
        disabled={loading}
        onChange={s => {
          segmentTransactionsFilterPaymentStatus({
            filterPaymentStatus: s.length === 0 ? [] : s.split(','),
          });
          changeFilters({ ...appliedFilters, status: s });
        }}
        options={status()}
        withApplyBtn
        hideValue
      />
    ),
    countryFilter: (
      <MultiSelect
        value={
          appliedFilters.country
            ? appliedFilters.country.split(',').map(countryCode => countries().find(co => co.id === countryCode))
            : []
        }
        name="filter-country"
        size="sm"
        label={l('transactions.filter.country')}
        titleOptions={l('transactions.filter.country.titleOptions')}
        placeholderSearch={l('searchCountries.label')}
        disabled={loading}
        onChange={countries => {
          segmentTransactionsFilterCountry({
            filterCountry: countries,
          });
          changeFilters({ ...appliedFilters, country: countries });
        }}
        options={countries()}
        withApplyBtn
        maxHeightOptions={400}
        hideValue
      />
    ),
    dateRangeFilter: (
      <SelectDateRange
        fromDate={appliedFilters.from}
        toDate={appliedFilters.to}
        onChangeRange={({ from, to }) => {
          segmentTransactionsFilterDate({
            filterFrom: from,
            filterTo: to,
          });
          changeFilters({ ...appliedFilters, from, to });
        }}
        size="small"
        label={l('transactions.filter.creationDate')}
        titleOptions={l('transactions.filter.creationDate.titleOptions')}
        disabled={loading}
        withApplyBtn
        hideValue
      />
    ),
  };
};

export default useComponentsFilter;
